import { ProfileCostEstimate } from '@features/Admin/Billing/ui/ProfileCostEstimate';
import React, { useReducer, ChangeEvent, useState, useMemo, useRef, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TableCell,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { IconChevronDown } from '@shared/assets/images/svgs';
import { useStyles } from './styles';
import { useStyles as useSharedStyles } from '../../styles';
import {
  AgentDto,
  AgentRequestDto,
  LlmProviderDto,
  TranscriberResponseDto,
  VoiceDto,
  useAgentProfileListControllerAgentProfileListForUsersQuery,
  useLlmModelListControllerModelListQuery,
} from '@shared/services/apiService/apiService';
import { useEffect } from 'react';
import { GuidedJobSetup, type GuidedJobSetupHandle } from '@features/Workspace/GuidedJobSetup';
import type { GuidedJobPlan } from '@features/Workspace/GuidedCreationReceiptProvider';
import { jobSectorLabel } from '@features/Workspace/jobMetadata';
import { useCreateAgent } from '@features/Admin/Agent/hooks/useCreateAgent';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '@shared/store';
import { selectors } from '@shared/store/ducks';
import { AgentProfileRequestDto } from '@features/Admin/Agent/model/types';
import { accentOptions, DEFAULT_NO_ROLE_AGENT_PROFILE, mapLanguageToISO639 } from './constants';
import { formatTranscriberLabel } from '@shared/utils/transcriberDisplay';
import { RolePicker } from './RolePicker';
import { VoicePicker } from './VoicePicker';
import { playVoicePreviewFromUserGesture } from '../playVoicePreview';
import {
  CompanySettingsLists,
  NO_REMOVED_AGENT_SETTINGS,
  RemovedAgentSettings,
  getRemovedAgentSettings,
  getUnavailableSelections,
  hasRemovedAgentSettings,
} from '@shared/utils/removedAgentSettings';

interface State {
  agent: string;
  sex: string;
  voice?: string;
  language: string;
  additionalLanguages: string[];
  transcriberId: string;
  profile?: AgentProfileRequestDto;
}

type FieldKey =
  | 'agent'
  | 'sex'
  | 'voice'
  | 'language'
  | 'transcriberId'
  | 'profile'
  | 'llmProviderId'
  | 'llmModel';

const FIELD_FOCUS_ORDER: FieldKey[] = [
  'agent',
  'sex',
  'voice',
  'language',
  'transcriberId',
  'llmProviderId',
  'llmModel',
  'profile',
];

enum ActionEnum {
  SET_AGENT = 'SET_AGENT',
  SET_SEX = 'SET_SEX',
  SET_VOICE = 'SET_VOICE',
  SET_LANGUAGE = 'SET_LANGUAGE',
  SET_ADDITIONAL_LANGUAGES = 'SET_ADDITIONAL_LANGUAGES',
  SET_TRANSCRIBER = 'SET_TRANSCRIBER',
  SET_PROFILE = 'SET_PROFILE',
  RESET = 'RESET',
}

type Action =
  | { type: ActionEnum.SET_AGENT; payload: string }
  | { type: ActionEnum.SET_SEX; payload: string }
  | { type: ActionEnum.SET_VOICE; payload?: string }
  | { type: ActionEnum.SET_LANGUAGE; payload: string }
  | { type: ActionEnum.SET_ADDITIONAL_LANGUAGES; payload: string[] }
  | { type: ActionEnum.SET_TRANSCRIBER; payload: string }
  | { type: ActionEnum.SET_PROFILE; payload?: AgentProfileRequestDto }
  | { type: ActionEnum.RESET };

const initialAgentState: State = {
  agent: '',
  sex: '',
  voice: '',
  language: '',
  additionalLanguages: [],
  transcriberId: '',
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionEnum.SET_AGENT:
      return { ...state, agent: action.payload };
    case ActionEnum.SET_SEX:
      return { ...state, sex: action.payload };
    case ActionEnum.SET_VOICE:
      return { ...state, voice: action.payload };
    case ActionEnum.SET_TRANSCRIBER:
      return { ...state, transcriberId: action.payload };
    case ActionEnum.SET_LANGUAGE:
      return {
        ...state,
        language: action.payload,
        additionalLanguages: state.additionalLanguages.includes(action.payload)
          ? normalizeLanguages([state.language, ...state.additionalLanguages]).filter(
              (language) => language !== action.payload,
            )
          : state.additionalLanguages,
      };
    case ActionEnum.SET_ADDITIONAL_LANGUAGES:
      return {
        ...state,
        additionalLanguages: normalizeLanguages(action.payload).filter(
          (language) => language !== state.language,
        ),
      };
    case ActionEnum.SET_PROFILE:
      return { ...state, profile: action.payload };
    case ActionEnum.RESET:
      return initialAgentState;
    default:
      return state;
  }
};

const normalizeLanguages = (languages: string[]) => [
  ...new Set(
    languages
      .map((language) => {
        const mapped = mapLanguageToISO639(language.trim());
        return (
          accentOptions.find((option) => option.value.toLowerCase() === mapped.toLowerCase())
            ?.value || mapped
        );
      })
      .filter(Boolean),
  ),
];

const buildEditAgentState = (
  agent: AgentDto,
  removed: RemovedAgentSettings,
  voiceData?: VoiceDto[],
): State => {
  const voice = removed.voice
    ? null
    : (voiceData?.find((v) => v.id === agent.voiceId) ?? agent.voice ?? null);

  const languages = normalizeLanguages(agent.languages || []);
  return {
    agent: agent.name,
    sex: voice?.gender || '',
    language: languages[0] || '',
    additionalLanguages: languages.slice(1),
    transcriberId: removed.transcriber ? '' : agent.transcriberId || agent.transcriber?.id || '',
    voice: voice?.id || '',
    profile: agent.profile || DEFAULT_NO_ROLE_AGENT_PROFILE,
  };
};

const CompositeFieldContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 0 }}>
      {children}
    </Box>
  );
};

const FieldErrorSlot: React.FC<{ message?: string; className: string }> = ({
  message,
  className,
}) => (
  <Typography className={className} sx={{ visibility: message ? 'visible' : 'hidden' }}>
    {message || '\u00A0'}
  </Typography>
);

const CustomSelectField: React.FC<{
  control: any;
  labelId: string;
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => void;
  placeholder: string;
  options: { key: string; value: string; label: string; icon?: React.ReactNode }[];
  disabled?: boolean;
  renderValue?: (selected: any) => React.ReactNode;
  className?: string;
  error?: boolean;
}> = ({
  control,
  labelId,
  name,
  value,
  onChange,
  placeholder,
  options,
  disabled,
  renderValue,
  className,
  error = false,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();

  const defaultRenderValue = (value: string) => {
    if (!value) {
      return <Typography color={theme.palette.extendedGrey?.neutral}>{placeholder}</Typography>;
    }
    return <>{value}</>;
  };

  return (
    <CustomSelect
      control={control}
      labelId={labelId}
      name={name}
      onChange={onChange}
      id={`${labelId}-id`}
      disabled={disabled}
      value={value}
      IconComponent={IconChevronDown}
      className={className || classes.select}
      error={error}
      displayEmpty
      inputProps={{ 'aria-label': placeholder }}
      renderValue={renderValue ? renderValue : defaultRenderValue}>
      {options.map((option) => (
        <MenuItem key={option.key} value={option.value} aria-label={option.label}>
          <Box className={classes.selectMenuItemContent}>
            {option.icon}
            {!option.icon && option.label}
          </Box>
        </MenuItem>
      ))}
    </CustomSelect>
  );
};

interface ManageAgentProps {
  onClose: () => Promise<void> | void;
  onCreated?: () => Promise<void>;
  agents: AgentDto[];
  agent: AgentDto | null;
  squadId?: string | null;
  transcriberData?: TranscriberResponseDto[];
  voiceData?: VoiceDto[];
  llmProviders?: LlmProviderDto[];
  isCompanyLoading?: boolean;
  isCompanyError?: boolean;
  guided?: boolean;
  blockedReason?: string;
  onDirtyChange?: (dirty: boolean) => void;
  campaignName?: string;
  presentation?: 'table' | 'panel';
  initialProfileId?: string;
  onSaved?: () => void;
  onSaveDraft?: (value: AgentRequestDto) => Promise<boolean>;
  allowJobDraft?: boolean;
  creationSessionId?: string;
  canContinueCreate?: () => boolean;
}

export const ManageAgent: React.FC<ManageAgentProps> = ({
  onClose,
  onCreated,
  agents,
  agent,
  squadId,
  transcriberData,
  voiceData,
  llmProviders,
  isCompanyLoading = false,
  isCompanyError = false,
  guided = false,
  blockedReason,
  onDirtyChange,
  campaignName,
  presentation = 'table',
  initialProfileId,
  onSaved,
  onSaveDraft,
  allowJobDraft = false,
  creationSessionId = 'legacy',
  canContinueCreate,
}) => {
  const asBlock = guided || presentation === 'panel';
  const companyLists = useMemo<CompanySettingsLists | undefined>(
    () =>
      isCompanyLoading
        ? undefined
        : { voices: voiceData, transcribers: transcriberData, llmProviders },
    [isCompanyLoading, voiceData, transcriberData, llmProviders],
  );

  const isWaitingForCompany =
    isCompanyLoading || isCompanyError || !llmProviders || !voiceData || !transcriberData;
  const [step, setStep] = useState(0);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [needsSaveReview, setNeedsSaveReview] = useState(false);
  const [creationConfirmed, setCreationConfirmed] = useState(false);
  const [isSavingDraft, setSavingDraft] = useState(false);
  const [jobPlan, setJobPlan] = useState<GuidedJobPlan | null>(null);
  const [jobCreationBlocked, setJobCreationBlocked] = useState(false);
  const [jobReceiptId, setJobReceiptId] = useState<string | null>(null);
  const jobSetup = useRef<GuidedJobSetupHandle>(null);
  const createLifecycle = useRef({
    alive: true,
    blocked: !!blockedReason,
    canContinue: canContinueCreate,
  });
  createLifecycle.current.blocked = !!blockedReason || isCompanyError;
  createLifecycle.current.canContinue = canContinueCreate;
  useEffect(() => {
    const lifecycle = createLifecycle.current;
    lifecycle.alive = true;
    return () => {
      lifecycle.alive = false;
    };
  }, []);
  const canFollowCreate = () =>
    createLifecycle.current.alive &&
    !createLifecycle.current.blocked &&
    (createLifecycle.current.canContinue?.() ?? true);
  const saving = useRef(false);
  useEffect(() => {
    if (!guided) return;
    onDirtyChange?.(true);
    return () => onDirtyChange?.(false);
  }, [guided, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const removedSettings = useMemo(
    () => (agent ? getRemovedAgentSettings(agent, companyLists) : NO_REMOVED_AGENT_SETTINGS),
    [agent, companyLists],
  );

  const [state, dispatch] = useReducer(
    reducer,
    agent ? buildEditAgentState(agent, removedSettings, voiceData) : initialAgentState,
  );

  const user = useAppSelector(selectors.auth.getUser);
  const { t } = useTranslation();
  const theme = useTheme();
  const hasNoLlmProviders = !isCompanyLoading && (llmProviders?.length ?? 0) === 0;
  const noLlmProvidersMessage = t(
    'adminMainPage.agents.fieldValidation.noLlmProvidersAvailable',
  ) as string;

  const { classes } = useStyles();
  const { classes: sharedClasses } = useSharedStyles();

  const { campaignId } = useParams();

  const {
    control,
    isLoading: isMutatingAgent,
    setValue,
    onSubmit,
  } = useCreateAgent(campaignId || '', agents, squadId);
  const isCreatingAgent = isMutatingAgent || isSavingDraft;

  const { currentData: agentProfileTypes, refetch: reloadJobCatalog } =
    useAgentProfileListControllerAgentProfileListForUsersQuery(
      {
        filter: guided && allowJobDraft ? 'system,custom,customer-template' : 'system,custom',
      },
      { refetchOnMountOrArgChange: true },
    );

  const agentProfileSelectList = useMemo(
    () => agentProfileTypes?.concat([DEFAULT_NO_ROLE_AGENT_PROFILE]),
    [agentProfileTypes],
  );

  // LLM config local state
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [providerAllowsCustom, setProviderAllowsCustom] = useState<boolean>(false);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [customModelKey, setCustomModelKey] = useState<string>('');

  const isSelectedProviderInCompany = Boolean(
    selectedProviderId && llmProviders?.some((p) => p.id === selectedProviderId),
  );
  const { data: llmModels, currentData: currentLlmModels } =
    useLlmModelListControllerModelListQuery(
      { id: selectedProviderId as string },
      { skip: !isSelectedProviderInCompany || providerAllowsCustom },
    );

  const [isPlayingVoice, setIsPlayingVoice] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [saveShake, setSaveShake] = useState(false);
  const [voiceResetHint, setVoiceResetHint] = useState(false);
  // Selections cleared because a refetched list no longer has them (agent's own or replacements)
  const [staleSelections, setStaleSelections] =
    useState<RemovedAgentSettings>(NO_REMOVED_AGENT_SETTINGS);
  const fieldAnchorRefs = useRef<Partial<Record<FieldKey, HTMLTableCellElement | null>>>({});
  // MUI types TableCell's ref as Ref<unknown>; the cell is always a table cell element
  const setFieldAnchor =
    (...keys: FieldKey[]) =>
    (el: unknown) => {
      for (const key of keys) {
        fieldAnchorRefs.current[key] = el as HTMLTableCellElement | null;
      }
    };
  const [fieldErrors, setFieldErrors] = useState<Record<FieldKey, string>>({
    agent: '',
    sex: '',
    voice: '',
    language: '',
    transcriberId: '',
    profile: '',
    llmProviderId: '',
    llmModel: '',
  });

  // Initialize the edit form once per agent, after the company lists are known. A later company
  // refetch must not erase a replacement the user already picked.
  const initializedAgentIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!agent || isWaitingForCompany) return;
    if (initializedAgentIdRef.current === agent.id) return;
    initializedAgentIdRef.current = agent.id;

    const next = buildEditAgentState(agent, removedSettings, voiceData);
    dispatch({ type: ActionEnum.SET_AGENT, payload: next.agent });
    dispatch({ type: ActionEnum.SET_SEX, payload: next.sex });
    dispatch({ type: ActionEnum.SET_VOICE, payload: next.voice });
    dispatch({ type: ActionEnum.SET_LANGUAGE, payload: next.language });
    dispatch({ type: ActionEnum.SET_ADDITIONAL_LANGUAGES, payload: next.additionalLanguages });
    dispatch({ type: ActionEnum.SET_TRANSCRIBER, payload: next.transcriberId });

    // Prefill LLM configuration
    if (removedSettings.llmProvider) {
      setSelectedProviderId('');
      setProviderAllowsCustom(false);
      setSelectedModelId('');
      setCustomModelKey('');
    } else if (llmProviders && agent.llmProviderId) {
      setSelectedProviderId(agent.llmProviderId);
      const provider = llmProviders.find((p) => p.id === agent.llmProviderId);
      const allowsCustom =
        provider?.allowsCustomModels ?? agent.llmProvider?.allowsCustomModels ?? false;
      setProviderAllowsCustom(allowsCustom);
      if (allowsCustom) {
        setCustomModelKey(agent.llmCustomModelKey || '');
        setSelectedModelId('');
      } else {
        setSelectedModelId(removedSettings.llmModel ? '' : agent.llmModelId || '');
        setCustomModelKey('');
      }
    }
  }, [agent, isWaitingForCompany, removedSettings, voiceData, llmProviders]);

  const profileInitializedAgent = useRef<string | null>(null);
  useEffect(() => {
    if (!agent || !agentProfileTypes || profileInitializedAgent.current === agent.id) return;
    profileInitializedAgent.current = agent.id;
    dispatch({
      type: ActionEnum.SET_PROFILE,
      payload:
        agentProfileSelectList?.find((p) => p.id === (agent.profileId || agent.profile?.id)) ||
        DEFAULT_NO_ROLE_AGENT_PROFILE,
    });
  }, [agent, agentProfileSelectList, agentProfileTypes]);

  const createInitialized = useRef(false);
  useEffect(() => {
    if (agent || createInitialized.current || isWaitingForCompany) return;
    createInitialized.current = true;
    dispatch({ type: ActionEnum.RESET });
    if (llmProviders && llmProviders.length > 0) {
      const firstProvider = llmProviders[0];
      setSelectedProviderId(firstProvider.id);
      setProviderAllowsCustom(firstProvider.allowsCustomModels);
      setSelectedModelId('');
      setCustomModelKey('');
    } else {
      setSelectedProviderId('');
      setProviderAllowsCustom(false);
      setSelectedModelId('');
      setCustomModelKey('');
    }
  }, [agent, isWaitingForCompany, llmProviders]);

  useEffect(() => {
    // Edit init effect owns provider selection for existing agents. Running fallback
    // here with a stale empty selectedProviderId can overwrite saved LLM provider.
    if (agent) return;

    if (!llmProviders?.length) {
      if (selectedProviderId) {
        setSelectedProviderId('');
        setProviderAllowsCustom(false);
        setSelectedModelId('');
        setCustomModelKey('');
      }
      return;
    }
    if (selectedProviderId && llmProviders.some((p) => p.id === selectedProviderId)) {
      return;
    }
    const fallback = llmProviders[0];
    setSelectedProviderId(fallback.id);
    setProviderAllowsCustom(fallback.allowsCustomModels);
    setSelectedModelId('');
    setCustomModelKey('');
  }, [agent, llmProviders, selectedProviderId]);

  useEffect(() => {
    if (!agent && !providerAllowsCustom && llmModels && llmModels.length > 0 && !selectedModelId) {
      setSelectedModelId(llmModels[0].id);
    }
  }, [agent, providerAllowsCustom, llmModels, selectedModelId]);

  useEffect(() => {
    if (agent) return;
    dispatch({ type: ActionEnum.SET_PROFILE, payload: DEFAULT_NO_ROLE_AGENT_PROFILE });
  }, [agent]);

  const initialProfileApplied = useRef(false);
  const awaitingInitialProfile = !!initialProfileId && !initialProfileApplied.current;
  useEffect(() => {
    if (
      !initialProfileId ||
      initialProfileApplied.current ||
      isWaitingForCompany ||
      (!agent && !createInitialized.current)
    )
      return;
    const profile = agentProfileSelectList?.find((item) => item.id === initialProfileId);
    if (!profile) return;
    initialProfileApplied.current = true;
    dispatch({ type: ActionEnum.SET_PROFILE, payload: profile });
    if (agent) onDirtyChange?.(true);
  }, [agent, initialProfileId, agentProfileSelectList, isWaitingForCompany, onDirtyChange]);

  useEffect(() => {
    if (agent || state.transcriberId || !transcriberData?.length) return;
    if (transcriberData.length === 1) {
      dispatch({ type: ActionEnum.SET_TRANSCRIBER, payload: transcriberData[0].id });
    }
  }, [agent, state.transcriberId, transcriberData]);

  useEffect(() => {
    if (agent || state.language) return;
    const defaultLanguage = accentOptions.find((option) => option.key === 'eng');
    if (defaultLanguage) {
      dispatch({ type: ActionEnum.SET_LANGUAGE, payload: defaultLanguage.key });
    }
  }, [agent, state.language]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  const stopAudioPlayback = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    }
  };

  const focusFirstError = useCallback((errors: Record<FieldKey, string>) => {
    const firstKey = FIELD_FOCUS_ORDER.find((key) => errors[key]);
    if (!firstKey) return;

    const cell = fieldAnchorRefs.current[firstKey];
    cell?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

    const focusable = cell?.querySelector<HTMLElement>(
      'input, button, [role="combobox"], .MuiSelect-select',
    );
    focusable?.focus({ preventScroll: true });
  }, []);

  const handleInputChange =
    (type: Action['type']) => (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      onDirtyChange?.(true);
      const clearFieldError = (field: FieldKey) => {
        if (!showValidation) return;
        setFieldErrors((prev) => ({ ...prev, [field]: '' }));
      };

      switch (type) {
        case ActionEnum.SET_SEX: {
          const hadVoice = Boolean(state.voice);
          dispatch({ type: ActionEnum.SET_SEX, payload: event.target.value as string });
          dispatch({ type: ActionEnum.SET_VOICE, payload: undefined });
          setVoiceResetHint(hadVoice);
          clearFieldError('sex');
          clearFieldError('voice');
          return;
        }

        case ActionEnum.SET_VOICE:
          dispatch({
            type: ActionEnum.SET_VOICE,
            payload: event.target.value as string,
          });
          clearFieldError('voice');
          return;

        case ActionEnum.SET_AGENT:
        case ActionEnum.SET_LANGUAGE:
          dispatch({ type, payload: event.target.value as string });
          clearFieldError(type === ActionEnum.SET_AGENT ? 'agent' : 'language');
          return;

        case ActionEnum.SET_TRANSCRIBER:
          dispatch({ type, payload: event.target.value as string });
          clearFieldError('transcriberId');
          return;

        default:
          // no need to handle RESET
          return;
      }
    };

  const dropUnavailableSelections = useCallback((): RemovedAgentSettings => {
    if (isWaitingForCompany) return NO_REMOVED_AGENT_SETTINGS;

    const unavailable = getUnavailableSelections(
      {
        voiceId: state.voice,
        transcriberId: state.transcriberId,
        llmProviderId: selectedProviderId,
        llmModelId: providerAllowsCustom ? null : selectedModelId,
      },
      {
        voices: voiceData,
        transcribers: transcriberData,
        llmProviders,
        llmModels: currentLlmModels,
      },
    );
    if (!hasRemovedAgentSettings(unavailable)) return unavailable;

    if (unavailable.voice) dispatch({ type: ActionEnum.SET_VOICE, payload: '' });
    if (unavailable.transcriber) dispatch({ type: ActionEnum.SET_TRANSCRIBER, payload: '' });
    if (unavailable.llmProvider) {
      setSelectedProviderId('');
      setProviderAllowsCustom(false);
      setCustomModelKey('');
    }
    if (unavailable.llmProvider || unavailable.llmModel) setSelectedModelId('');
    setStaleSelections((prev) => ({
      voice: prev.voice || unavailable.voice,
      transcriber: prev.transcriber || unavailable.transcriber,
      llmProvider: prev.llmProvider || unavailable.llmProvider,
      llmModel: prev.llmModel || unavailable.llmModel,
    }));
    return unavailable;
  }, [
    isWaitingForCompany,
    state.voice,
    state.transcriberId,
    selectedProviderId,
    providerAllowsCustom,
    selectedModelId,
    voiceData,
    transcriberData,
    llmProviders,
    currentLlmModels,
  ]);

  // A background refetch can remove a selected voice, transcriber, provider or model. Also runs on
  // selection changes, which is a no-op: the user can pick only values that are in the lists.
  useEffect(() => {
    dropUnavailableSelections();
  }, [dropUnavailableSelections]);

  type ValidatedValues = {
    voice?: string;
    transcriberId: string;
    selectedProviderId: string;
    selectedModelId: string;
    customModelKey: string;
    providerAllowsCustom: boolean;
  };

  const validateRequiredFields = (
    values: ValidatedValues = {
      voice: state.voice,
      transcriberId: state.transcriberId,
      selectedProviderId,
      selectedModelId,
      customModelKey,
      providerAllowsCustom,
    },
    fields: FieldKey[] = FIELD_FOCUS_ORDER,
  ): boolean => {
    const isProfileSelected =
      !!(guided && allowJobDraft && jobPlan) ||
      Boolean(
        state.profile?.id &&
        agentProfileSelectList?.some((profile) => profile.id === state.profile?.id),
      );

    const nextErrors: Record<FieldKey, string> = {
      agent: state.agent?.trim()
        ? ''
        : (t('adminMainPage.agents.fieldValidation.enterHumanName') as string),
      sex: state.sex ? '' : (t('adminMainPage.agents.fieldValidation.selectSex') as string),
      voice:
        state.sex && !values.voice
          ? (t('adminMainPage.agents.fieldValidation.selectVoice') as string)
          : '',
      language: state.language
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectLanguage') as string),
      transcriberId: values.transcriberId
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectTranscriber') as string),
      profile: isProfileSelected
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectProfile') as string),
      llmProviderId: values.selectedProviderId
        ? ''
        : hasNoLlmProviders
          ? noLlmProvidersMessage
          : (t('adminMainPage.agents.fieldValidation.selectLlmProvider') as string),
      llmModel: !values.selectedProviderId
        ? ''
        : values.providerAllowsCustom && !values.customModelKey?.trim()
          ? (t('adminMainPage.agents.fieldValidation.enterCustomLlmModel') as string)
          : !values.providerAllowsCustom && !values.selectedModelId
            ? (t('adminMainPage.agents.fieldValidation.selectLlmModel') as string)
            : '',
    };

    setFieldErrors(nextErrors);

    const isValid = fields.every((key) => !nextErrors[key]);
    if (!isValid) {
      if (guided && fields === FIELD_FOCUS_ORDER)
        setStep(
          nextErrors.profile
            ? 0
            : nextErrors.sex || nextErrors.voice || nextErrors.language
              ? 1
              : 2,
        );
      const visibleErrors = { ...nextErrors };
      FIELD_FOCUS_ORDER.forEach((key) => {
        if (!fields.includes(key)) visibleErrors[key] = '';
      });
      window.requestAnimationFrame(() => focusFirstError(visibleErrors));
    }

    return isValid;
  };

  const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (
      isWaitingForCompany ||
      awaitingInitialProfile ||
      blockedReason ||
      saving.current ||
      needsSaveReview ||
      jobCreationBlocked ||
      creationConfirmed ||
      (guided && step !== 3)
    )
      return;

    if (guided && allowJobDraft && jobPlan && !jobSetup.current?.validate()) {
      setStep(0);
      return;
    }
    stopAudioPlayback();
    setShowValidation(true);

    const unavailable = dropUnavailableSelections();
    const isProviderKept = !unavailable.llmProvider;
    const isValid = validateRequiredFields({
      voice: unavailable.voice ? '' : state.voice,
      transcriberId: unavailable.transcriber ? '' : state.transcriberId,
      selectedProviderId: isProviderKept ? selectedProviderId : '',
      selectedModelId: isProviderKept && !unavailable.llmModel ? selectedModelId : '',
      customModelKey: isProviderKept ? customModelKey : '',
      providerAllowsCustom: isProviderKept && providerAllowsCustom,
    });

    if (!isValid) {
      setSaveShake(false);
      window.requestAnimationFrame(() => {
        setSaveShake(true);
      });
      window.setTimeout(() => setSaveShake(false), 650);
      return;
    }

    setValue('name', state.agent);
    setValue('voiceId', state?.voice);
    setValue('transcriberId', state?.transcriberId); // TODO: to be implemented CLARIFY WITH MAXIM
    setValue('languages', normalizeLanguages([state.language, ...state.additionalLanguages]));
    setValue('profile', state.profile);
    setValue('profileId', state.profile?.id);

    // Map LLM config to form values according to rules
    setValue('llmProviderId', selectedProviderId);
    if (providerAllowsCustom) {
      // If provider allows custom models -> send only custom key
      setValue('llmCustomModelKey', customModelKey || null);
      setValue('llmModelId', null);
    } else {
      // If provider does not allow custom models -> send model id
      setValue('llmModelId', selectedModelId || null);
      setValue('llmCustomModelKey', null);
    }

    if (agent) {
      setValue('id', agent?.id || '');
    }

    saving.current = true;
    setSavingDraft(true);
    const jobCreation = guided && allowJobDraft && jobPlan ? jobSetup.current : null;
    try {
      const resolvedJob = jobCreation ? await jobCreation.resolve() : null;
      if ((jobCreation && !resolvedJob) || !canFollowCreate()) return;
      if (resolvedJob) {
        setValue('profile', resolvedJob);
        setValue('profileId', resolvedJob.id);
      }
      jobCreation?.markAgentStarted();
      const submit = onSaveDraft
        ? () =>
            onSaveDraft({
              name: state.agent,
              voiceId: state.voice!,
              transcriberId: state.transcriberId,
              languages: normalizeLanguages([state.language, ...state.additionalLanguages]),
              profileId: resolvedJob?.id ?? state.profile?.id ?? null,
              llmProviderId: selectedProviderId,
              llmModelId: providerAllowsCustom ? null : selectedModelId || null,
              llmCustomModelKey: providerAllowsCustom ? customModelKey || null : null,
            })
        : onSubmit;
      let success: boolean;
      try {
        success = await submit();
      } catch {
        jobCreation?.markAgentUnknown();
        if (createLifecycle.current.alive && guided) setNeedsSaveReview(true);
        return;
      }
      if (!success) {
        jobCreation?.markAgentUnknown();
        if (createLifecycle.current.alive && guided) setNeedsSaveReview(true);
        return;
      }
      jobCreation?.complete();
      if (createLifecycle.current.alive && guided) {
        setCreationConfirmed(true);
        onDirtyChange?.(false);
      }
      if (!canFollowCreate()) return;
      dispatch({ type: ActionEnum.RESET });
      onDirtyChange?.(false);
      setShowValidation(false);
      setVoiceResetHint(false);
      if (!agent && onCreated) {
        await onCreated();
        return;
      }
      if (onSaved) {
        onSaved();
        return;
      }
      await onClose();
    } finally {
      saving.current = false;
      if (createLifecycle.current.alive) setSavingDraft(false);
    }
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    stopAudioPlayback();

    if (guided) {
      setConfirmDiscard(true);
      return;
    }
    if (presentation === 'panel') {
      void onClose();
      return;
    }

    onClose();
    dispatch({ type: ActionEnum.RESET });
    setShowValidation(false);
    setVoiceResetHint(false);
  };

  const showVoiceRemovedHint =
    (removedSettings.voice || staleSelections.voice) && !state.voice && !voiceResetHint;
  const showTranscriberRemovedHint =
    (removedSettings.transcriber || staleSelections.transcriber) && !state.transcriberId;
  const showLlmProviderRemovedHint =
    (removedSettings.llmProvider || staleSelections.llmProvider) && !selectedProviderId;
  const showLlmModelRemovedHint =
    ((removedSettings.llmModel && selectedProviderId === agent?.llmProviderId) ||
      staleSelections.llmModel) &&
    !providerAllowsCustom &&
    !selectedModelId;

  const waitingForCompanyMessage = isWaitingForCompany
    ? (t(
        isCompanyError
          ? 'adminMainPage.agents.fieldValidation.companySettingsUnavailable'
          : 'adminMainPage.agents.fieldValidation.companySettingsLoading',
      ) as string)
    : undefined;
  const saveTooltip =
    waitingForCompanyMessage ??
    (showValidation && Object.values(fieldErrors).some(Boolean)
      ? (t('adminMainPage.agents.fieldValidation.fixErrorsBeforeSave') as string)
      : (t('common.save') as string));

  const sexOptions = Array.from(new Set(voiceData?.map((voice) => voice.gender))).map((gender) => {
    const genderLower = (gender || '').toLowerCase();
    return {
      key: gender || '',
      value: gender || '',
      label: (gender ?? '').charAt(0).toUpperCase() + (gender ?? '').slice(1) || '',
      icon:
        genderLower === 'male' ? (
          <MaleIcon className={classes.maleIcon} />
        ) : genderLower === 'female' ? (
          <FemaleIcon className={classes.femaleIcon} />
        ) : null,
    };
  });

  const filteredVoices = useMemo(
    () => voiceData?.filter((voice) => voice.gender === state.sex) || [],
    [voiceData, state.sex],
  );

  const transcriberOptions =
    transcriberData?.map((transcriber) => {
      return {
        key: transcriber.id,
        value: transcriber.id,
        label: formatTranscriberLabel(transcriber),
      };
    }) || [];

  const playVoicePreview = (previewUrl: string) => {
    if (isPlayingVoice || !previewUrl) return;

    stopAudioPlayback();

    const stopUi = () => {
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    };
    const audio = playVoicePreviewFromUserGesture(previewUrl, stopUi);
    if (!audio) return;
    audio.addEventListener('error', stopUi);
    audio.addEventListener('ended', stopUi);

    setIsPlayingVoice(true);
    setCurrentAudio(audio);
  };

  const handleVoiceIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (isPlayingVoice && currentAudio) {
      stopAudioPlayback();
      return;
    }

    if (!state.sex || !state.voice) {
      console.warn('Please select a gender and voice first');
      return;
    }

    const selectedVoice = voiceData?.find((voice) => voice.id === state.voice);
    if (!selectedVoice?.previewUrl) {
      console.warn('No preview URL available for this voice');
      return;
    }

    playVoicePreview(selectedVoice.previewUrl);
  };

  if (!user?.companyId) {
    return <div>User not found...</div>;
  }

  const idField = <TableCell className={sharedClasses.agentIdCell} sx={{ padding: '8px' }} />;
  const startField = <TableCell className={sharedClasses.startCell} sx={{ padding: '8px' }} />;
  const nameField = (
    <TableCell
      component={asBlock ? 'div' : 'td'}
      ref={setFieldAnchor('agent')}
      className={asBlock ? classes.blockField : sharedClasses.nameCell}>
      <CustomTextField
        control={control}
        name="name"
        value={state.agent}
        onChange={handleInputChange(ActionEnum.SET_AGENT)}
        id="agent-name"
        type="text"
        placeholder={t('campaignPage.placeholders.humanName')}
        varient="outlined"
        inputProps={{ maxLength: 56 }}
        fullWidth
        required
        error={showValidation && Boolean(fieldErrors.agent)}
        helperText={showValidation ? fieldErrors.agent : ''}
        disabled={isPlayingVoice || !!blockedReason}
      />
    </TableCell>
  );
  const voiceField = (
    <TableCell
      component={asBlock ? 'div' : 'td'}
      ref={setFieldAnchor('sex', 'voice')}
      className={asBlock ? classes.blockField : sharedClasses.voiceCell}>
      <CompositeFieldContainer>
        <Box sx={{ minWidth: 0 }}>
          <CustomSelectField
            control={control}
            labelId="sex-type"
            name="sexType"
            value={state.sex}
            onChange={handleInputChange(ActionEnum.SET_SEX)}
            placeholder={t('campaignPage.placeholders.sexType')}
            options={sexOptions}
            disabled={isPlayingVoice || !!blockedReason}
            className={classes.sexSelect}
            error={showValidation && Boolean(fieldErrors.sex)}
            renderValue={(selected) => {
              const selectedSex = sexOptions?.find((option) => option.key === selected);
              return (
                <Box className={classes.genderSelectContainer}>
                  {selectedSex?.icon || selectedSex?.label || ''}
                </Box>
              );
            }}
          />
          {showValidation && fieldErrors.sex ? (
            <FieldErrorSlot className={classes.fieldErrorText} message={fieldErrors.sex} />
          ) : null}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box className={classes.voiceContainer}>
            <Box className={classes.voiceSelectWrapper}>
              {(() => {
                const selectedVoice = voiceData?.find((voice) => voice.id === state.voice);
                const hasPreview = selectedVoice?.previewUrl;
                const canPlay = state.sex && state.voice && hasPreview;
                const isDisabled = !state.sex || !state.voice;

                const iconClasses = [
                  classes.voiceIcon,
                  isPlayingVoice
                    ? classes.voiceIconPlaying
                    : hasPreview
                      ? classes.voiceIconWithPreview
                      : classes.voiceIconNoPreview,
                  isDisabled ? classes.voiceIconDisabled : '',
                  isPlayingVoice ? classes.voiceIconAnimated : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <Tooltip
                    title={
                      isDisabled
                        ? t('campaignPage.placeholders.selectVoiceFirst')
                        : hasPreview
                          ? t('adminMainPage.agents.voicePreviewAvailable')
                          : t('adminMainPage.agents.noVoicePreview')
                    }
                    arrow
                    placement="top">
                    <Box
                      role="button"
                      aria-label={
                        hasPreview
                          ? t('adminMainPage.agents.voicePreviewAvailable')
                          : t('adminMainPage.agents.noVoicePreview')
                      }
                      onClick={canPlay ? handleVoiceIconClick : undefined}
                      className={iconClasses}>
                      {hasPreview ? (
                        <VolumeUpIcon className={classes.volumeIcon} />
                      ) : (
                        <VolumeOffIcon className={classes.volumeIcon} />
                      )}
                    </Box>
                  </Tooltip>
                );
              })()}
              <VoicePicker
                voices={filteredVoices}
                selectedVoiceId={state.voice || ''}
                disabled={!state.sex || isPlayingVoice || !!blockedReason}
                error={showValidation && Boolean(fieldErrors.voice)}
                placeholder={t('campaignPage.placeholders.voiceType')}
                onChange={(voiceId) => {
                  onDirtyChange?.(true);
                  dispatch({
                    type: ActionEnum.SET_VOICE,
                    payload: voiceId,
                  });
                  setVoiceResetHint(false);
                  if (showValidation) {
                    setFieldErrors((prev) => ({ ...prev, voice: '' }));
                  }
                }}
              />
            </Box>
          </Box>
          {showValidation && !fieldErrors.sex && fieldErrors.voice ? (
            <FieldErrorSlot className={classes.fieldErrorText} message={fieldErrors.voice} />
          ) : null}
        </Box>
        {!state.sex && !showValidation && !removedSettings.voice ? (
          <Typography className={classes.fieldHintText} sx={{ color: 'text.secondary' }}>
            {t('adminMainPage.agents.fieldValidation.selectSexFirst')}
          </Typography>
        ) : null}
        {showVoiceRemovedHint ? (
          <Typography className={classes.fieldHintText}>
            {t('adminMainPage.agents.fieldValidation.voiceRemovedChooseReplacement')}
          </Typography>
        ) : null}
        {voiceResetHint ? (
          <Typography className={classes.fieldHintText}>
            {t('adminMainPage.agents.fieldValidation.voiceResetHint')}
          </Typography>
        ) : null}
      </CompositeFieldContainer>
    </TableCell>
  );
  const languageField = (
    <TableCell
      component={asBlock ? 'div' : 'td'}
      ref={setFieldAnchor('language')}
      className={asBlock ? classes.blockField : sharedClasses.languageCell}>
      <CustomSelectField
        control={control}
        labelId="language-type"
        name="languageType"
        value={state.language}
        onChange={handleInputChange(ActionEnum.SET_LANGUAGE)}
        placeholder={t('campaignPage.placeholders.languageType')}
        options={accentOptions.concat(
          state.language && !accentOptions.some((option) => option.key === state.language)
            ? [{ key: state.language, value: state.language, label: state.language + ' (saved)' }]
            : [],
        )}
        error={showValidation && Boolean(fieldErrors.language)}
        disabled={isPlayingVoice || !!blockedReason}
        renderValue={(selected) => {
          const selectedLang = accentOptions?.find((lang) => lang.key === selected);
          return selectedLang?.label || selected || '';
        }}
      />
      <FieldErrorSlot
        className={classes.fieldErrorText}
        message={showValidation ? fieldErrors.language : ''}
      />
      {asBlock && (
        <Stack gap={1} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            The language above is primary. Add or remove other conversation languages below.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Additional languages"
            value={state.additionalLanguages}
            SelectProps={{ multiple: true }}
            disabled={isPlayingVoice || !!blockedReason || isWaitingForCompany || isCreatingAgent}
            onChange={(event) => {
              const values =
                typeof event.target.value === 'string'
                  ? event.target.value.split(',')
                  : event.target.value;
              const allowed = new Set([
                ...accentOptions.map((option) => option.value),
                ...state.additionalLanguages,
              ]);
              onDirtyChange?.(true);
              dispatch({
                type: ActionEnum.SET_ADDITIONAL_LANGUAGES,
                payload: values.filter((value) => allowed.has(value)),
              });
            }}>
            {accentOptions
              .filter((option) => option.value !== state.language)
              .map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            {state.additionalLanguages
              .filter((value) => !accentOptions.some((option) => option.value === value))
              .map((value) => (
                <MenuItem key={value} value={value}>
                  {value} (saved)
                </MenuItem>
              ))}
          </TextField>
        </Stack>
      )}
    </TableCell>
  );
  const transcriberField = (
    <TableCell
      component={asBlock ? 'div' : 'td'}
      ref={setFieldAnchor('transcriberId')}
      className={asBlock ? classes.blockField : sharedClasses.transcriberCell}>
      <CustomSelectField
        control={control}
        labelId="transcriber-provider"
        name="transcriberId"
        value={state.transcriberId}
        onChange={handleInputChange(ActionEnum.SET_TRANSCRIBER)}
        placeholder={t('campaignPage.placeholders.transcriberType')}
        options={transcriberOptions}
        error={showValidation && Boolean(fieldErrors.transcriberId)}
        disabled={isPlayingVoice || !!blockedReason}
        renderValue={(selected) => {
          const selectedTranscriber = transcriberOptions?.find(
            (transcriber) => transcriber.key === selected,
          );
          return selectedTranscriber?.label || '';
        }}
      />
      {showTranscriberRemovedHint ? (
        <Typography className={classes.fieldHintText}>
          {t('adminMainPage.agents.fieldValidation.transcriberRemovedChooseReplacement')}
        </Typography>
      ) : null}
      <FieldErrorSlot
        className={classes.fieldErrorText}
        message={showValidation ? fieldErrors.transcriberId : ''}
      />
    </TableCell>
  );
  const modelField = (
    <TableCell
      component={asBlock ? 'div' : 'td'}
      ref={setFieldAnchor('llmProviderId', 'llmModel')}
      className={asBlock ? classes.blockField : sharedClasses.llmCell}>
      <CompositeFieldContainer>
        <Tooltip
          title={hasNoLlmProviders ? noLlmProvidersMessage : ''}
          arrow
          placement="top"
          disableHoverListener={!hasNoLlmProviders}>
          <Box component="span" sx={{ display: 'block', width: '100%' }}>
            <CustomSelectField
              control={control}
              labelId="llm-provider"
              name="llmProviderId"
              value={selectedProviderId}
              onChange={(e) => {
                onDirtyChange?.(true);
                const value = (e.target as HTMLInputElement).value as string;
                setSelectedProviderId(value);
                const provider = llmProviders?.find((p) => p.id === value);
                const allows = provider?.allowsCustomModels ?? false;
                setProviderAllowsCustom(allows);
                // Reset incompatible fields when provider changes
                setCustomModelKey('');
                setSelectedModelId('');
                setStaleSelections((prev) => ({ ...prev, llmModel: false }));
                if (showValidation) {
                  setFieldErrors((prev) => ({ ...prev, llmProviderId: '', llmModel: '' }));
                }
              }}
              placeholder={
                hasNoLlmProviders
                  ? (t('adminMainPage.agents.noneAvailable') as string)
                  : (t('adminMainPage.agents.llmProvider') as string)
              }
              options={llmProviders?.map((p) => ({ key: p.id, value: p.id, label: p.title })) || []}
              error={showValidation && Boolean(fieldErrors.llmProviderId)}
              disabled={isPlayingVoice || hasNoLlmProviders || !!blockedReason}
              renderValue={(selected) => {
                if (hasNoLlmProviders) {
                  const emptyLabel = t('adminMainPage.agents.noneAvailable') as string;
                  return (
                    <Tooltip title={noLlmProvidersMessage} arrow placement="top">
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {emptyLabel}
                      </Box>
                    </Tooltip>
                  );
                }
                const selectedProvider = llmProviders?.find((p) => p.id === selected);

                const agentProviderTitle =
                  selected && selected === agent?.llmProviderId && !removedSettings.llmProvider
                    ? agent?.llmProvider?.title
                    : undefined;
                const label =
                  selectedProvider?.title ||
                  agentProviderTitle ||
                  (t('adminMainPage.agents.llmProvider') as string);
                return (
                  <Tooltip title={label} arrow placement="top">
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {label}
                    </Box>
                  </Tooltip>
                );
              }}
            />
          </Box>
        </Tooltip>
        {hasNoLlmProviders ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
            {noLlmProvidersMessage}
          </Typography>
        ) : null}
        {showLlmProviderRemovedHint ? (
          <Typography className={classes.fieldHintText}>
            {t('adminMainPage.agents.fieldValidation.llmProviderRemovedChooseReplacement')}
          </Typography>
        ) : null}

        {selectedProviderId &&
          (providerAllowsCustom ? (
            <>
              <CustomTextField
                control={control}
                name="llmCustomModelKey"
                value={customModelKey}
                onChange={(e: any) => {
                  onDirtyChange?.(true);
                  setCustomModelKey(e.target.value);
                  if (showValidation) {
                    setFieldErrors((prev) => ({ ...prev, llmModel: '' }));
                  }
                }}
                id="llm-custom-model-key"
                type="text"
                placeholder={t('adminMainPage.agents.llmCustomModelKey')}
                varient="outlined"
                fullWidth
                required
                error={showValidation && Boolean(fieldErrors.llmModel)}
                disabled={isPlayingVoice || !!blockedReason}
              />
              <FieldErrorSlot
                className={classes.fieldErrorText}
                message={showValidation ? fieldErrors.llmModel : ''}
              />
            </>
          ) : (
            <CustomSelectField
              control={control}
              labelId="llm-model"
              name="llmModelId"
              value={selectedModelId}
              onChange={(e) => {
                onDirtyChange?.(true);
                setSelectedModelId((e.target as HTMLInputElement).value as string);
                if (showValidation) {
                  setFieldErrors((prev) => ({ ...prev, llmModel: '' }));
                }
              }}
              placeholder={t('adminMainPage.agents.llmModel')}
              options={llmModels?.map((m) => ({ key: m.id, value: m.id, label: m.title })) || []}
              error={showValidation && Boolean(fieldErrors.llmModel)}
              disabled={isPlayingVoice || !!blockedReason}
              renderValue={(selected) => {
                const selectedModel = llmModels?.find((m) => m.id === selected);
                const label =
                  selectedModel?.title || (t('adminMainPage.agents.llmModel') as string);
                return (
                  <Tooltip title={label} arrow placement="top">
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {label}
                    </Box>
                  </Tooltip>
                );
              }}
            />
          ))}
        {showLlmModelRemovedHint ? (
          <Typography className={classes.fieldHintText}>
            {t('adminMainPage.agents.fieldValidation.llmModelRemovedChooseReplacement')}
          </Typography>
        ) : null}
        <FieldErrorSlot
          className={classes.fieldErrorText}
          message={showValidation ? fieldErrors.llmProviderId : ''}
        />
        {!providerAllowsCustom ? (
          <FieldErrorSlot
            className={classes.fieldErrorText}
            message={showValidation ? fieldErrors.llmModel : ''}
          />
        ) : null}
      </CompositeFieldContainer>
    </TableCell>
  );
  const jobField = (
    <TableCell
      component={asBlock ? 'div' : 'td'}
      ref={setFieldAnchor('profile')}
      className={asBlock ? classes.blockField : sharedClasses.profileCell}>
      <RolePicker
        profiles={agentProfileSelectList}
        selected={
          agentProfileSelectList?.find((profile) => profile.id === state?.profile?.id) ?? null
        }
        disabled={isPlayingVoice || !!blockedReason}
        error={showValidation && Boolean(fieldErrors.profile)}
        onChange={(profile) => {
          onDirtyChange?.(true);
          dispatch({
            type: ActionEnum.SET_PROFILE,
            payload: profile as AgentProfileRequestDto,
          });
          if (showValidation) {
            setFieldErrors((prev) => ({ ...prev, profile: '' }));
          }
        }}
      />
      <FieldErrorSlot
        className={classes.fieldErrorText}
        message={showValidation ? fieldErrors.profile : ''}
      />
    </TableCell>
  );
  const actionField = (
    <TableCell className={sharedClasses.actionsCell}>
      <Box
        display="flex"
        flexDirection={'column'}
        justifyContent="flex-end"
        alignItems="center"
        gap={1}>
        <Tooltip title={saveTooltip} arrow placement="left">
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <button
              className={classNames(classes.save_button, {
                [classes.save_button_shake]: saveShake,
              })}
              disabled={isCreatingAgent || isWaitingForCompany}
              onClick={handleSave}
              type="button"
              aria-label={t('common.save') as string}
              aria-description={waitingForCompanyMessage}>
              <CheckIcon htmlColor="white" style={{ width: 16 }} />
            </button>
          </Box>
        </Tooltip>

        <button className={classes.cancel_button} color="secondary" onClick={handleCancel}>
          <CloseIcon htmlColor={theme.palette.text.secondary} style={{ width: 16 }} />
        </button>
      </Box>
    </TableCell>
  );

  const advance = () => {
    stopAudioPlayback();
    setShowValidation(true);
    const fields: FieldKey[][] = [
      ['profile'],
      ['sex', 'voice', 'language'],
      ['agent', 'transcriberId', 'llmProviderId', 'llmModel'],
    ];
    if (step === 0 && guided && allowJobDraft && jobPlan && !jobSetup.current?.validate()) return;
    if (validateRequiredFields(undefined, fields[step])) {
      setShowValidation(false);
      setStep((current) => Math.min(3, current + 1));
    }
  };
  if (guided)
    return (
      <TableCell colSpan={9}>
        <Stack
          component="section"
          aria-label="Assistant setup"
          gap={3}
          sx={{ p: { xs: 1, md: 3 }, maxWidth: 900, mx: 'auto' }}>
          <Typography variant="h4">Create an assistant</Typography>
          <Typography color="text.secondary">
            Choose saved instructions and company settings. The assistant is created in this
            campaign only after you confirm the review.
          </Typography>
          <Stepper activeStep={step} alternativeLabel>
            {['Job', 'Voice', 'Details', 'Review'].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {blockedReason && <Alert severity="warning">{blockedReason}</Alert>}
          {awaitingInitialProfile && (
            <Alert severity="warning">
              Waiting for the selected job in the current editable catalog. If it is unavailable,
              reopen setup from the current Job library.
            </Alert>
          )}
          {isCompanyError && (
            <Alert severity="error">
              Current company settings could not be loaded. Close this setup and reload before
              saving.
            </Alert>
          )}
          {creationConfirmed && (
            <Alert severity="success">
              Agent creation was confirmed in this campaign. This setup will not submit again.{' '}
              <Button
                component="a"
                href={`/admin/campaign/${encodeURIComponent(campaignId || '')}/edit`}
                target="_blank"
                rel="noopener noreferrer">
                Inspect created assistant
              </Button>
            </Alert>
          )}
          {needsSaveReview && (
            <Alert severity="warning">
              Saving was not confirmed or the result is awaiting refresh. Close this setup and
              refresh the campaign agent list before creating another assistant.
            </Alert>
          )}
          {allowJobDraft && (
            <GuidedJobSetup
              ref={jobSetup}
              sessionKey={JSON.stringify([campaignId, creationSessionId])}
              campaignId={campaignId || ''}
              plan={jobPlan}
              onPlanChange={setJobPlan}
              source={
                agentProfileTypes?.find((profile) => profile.id === state.profile?.id) ?? null
              }
              disabled={!!blockedReason || isWaitingForCompany || needsSaveReview}
              busy={isCreatingAgent}
              activeStep={step === 0}
              savedJobField={jobField}
              canContinue={canFollowCreate}
              loadSource={async (id) => {
                const result = await reloadJobCatalog();
                if (result.error) throw new Error('Current Job catalog unavailable.');
                return result.data?.find((profile) => profile.id === id);
              }}
              onBlockedChange={setJobCreationBlocked}
              onReceiptChange={setJobReceiptId}
            />
          )}
          <Box
            component="fieldset"
            disabled={
              isCreatingAgent ||
              !!blockedReason ||
              needsSaveReview ||
              jobCreationBlocked ||
              creationConfirmed
            }
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            <Box sx={{ display: step === 0 ? 'block' : 'none' }}>
              <Typography variant="subtitle1">Reusable job</Typography>
              {!allowJobDraft && jobField}
            </Box>
            <Stack gap={2} sx={{ display: step === 1 ? 'flex' : 'none' }}>
              <Typography variant="subtitle1">Voice and language</Typography>
              {voiceField}
              {languageField}
            </Stack>
            <Stack gap={2} sx={{ display: step === 2 ? 'flex' : 'none' }}>
              <Typography variant="subtitle1">Name, transcriber and language model</Typography>
              {nameField}
              {transcriberField}
              {modelField}
            </Stack>
            {step === 3 && (
              <Stack gap={2}>
                <Typography variant="h5">Review your assistant</Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}>
                  {[
                    ['Name', state.agent],
                    ['Campaign', campaignName || campaignId],
                    ['Job', jobPlan?.values.name || state.profile?.name],
                    ['Voice', voiceData?.find((voice) => voice.id === state.voice)?.name],
                    [
                      'Languages',
                      normalizeLanguages([state.language, ...state.additionalLanguages])
                        .map(
                          (value) =>
                            accentOptions.find((language) => language.key === value)?.label ||
                            value,
                        )
                        .join(', '),
                    ],
                    [
                      'Transcriber',
                      transcriberOptions?.find((option) => option.key === state.transcriberId)
                        ?.label,
                    ],
                    [
                      'LLM provider',
                      llmProviders?.find((provider) => provider.id === selectedProviderId)?.title,
                    ],
                    [
                      'LLM model',
                      providerAllowsCustom
                        ? customModelKey
                        : llmModels?.find((model) => model.id === selectedModelId)?.title,
                    ],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography sx={{ overflowWrap: 'anywhere' }}>
                        {value || 'Not selected'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {jobPlan && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack gap={1}>
                      <Typography variant="subtitle1">
                        New custom Job · {jobPlan.values.name}
                      </Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {jobPlan.values.prompt}
                      </Typography>
                      <Typography>
                        Opening message: {jobPlan.values.firstMessage || 'Not configured'}
                      </Typography>
                      <Typography>
                        Opening interruptions:{' '}
                        {jobPlan.values.firstMessageInterruptionsEnabled ? 'Allowed' : 'Disabled'}
                      </Typography>
                      {jobPlan.values.sector !== undefined && (
                        <Typography>Sector: {jobSectorLabel(jobPlan.values.sector)}</Typography>
                      )}
                      {jobPlan.values.summary !== undefined && (
                        <Typography>
                          Summary: {jobPlan.values.summary || 'Not configured'}
                        </Typography>
                      )}
                      <Typography>
                        {jobPlan.taskFlow?.tasks.length || 0} workflow tasks{' '}
                        {jobPlan.mode === 'copy' ? 'copied in full' : ''}
                      </Typography>
                    </Stack>
                  </Paper>
                )}
                <Alert severity="info">
                  {jobPlan ? (
                    'Confirming saves a new custom Job and its complete workflow before creating the agent in this existing campaign. Partial receipts remain visible if a later step fails. Nothing starts automatically.'
                  ) : (
                    <>
                      This creates an agent only. It does not start the campaign, make a test call
                      or change the selected job.
                    </>
                  )}
                </Alert>
              </Stack>
            )}
          </Box>
          <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
            <Button onClick={handleCancel} disabled={isCreatingAgent}>
              Cancel setup
            </Button>
            <Stack direction="row" gap={1}>
              {step > 0 && (
                <Button
                  disabled={isCreatingAgent}
                  onClick={() => {
                    stopAudioPlayback();
                    setStep((current) => current - 1);
                  }}>
                  Back
                </Button>
              )}
              {step < 3 ? (
                <Button
                  variant="contained"
                  disabled={
                    isCreatingAgent ||
                    isWaitingForCompany ||
                    awaitingInitialProfile ||
                    !!blockedReason ||
                    needsSaveReview ||
                    jobCreationBlocked ||
                    creationConfirmed
                  }
                  onClick={advance}>
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={
                    isCreatingAgent ||
                    isWaitingForCompany ||
                    awaitingInitialProfile ||
                    !!blockedReason ||
                    needsSaveReview ||
                    jobCreationBlocked ||
                    creationConfirmed
                  }
                  onClick={handleSave}>
                  {jobReceiptId ? 'Resume assistant creation' : 'Confirm and create assistant'}
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Dialog
          open={confirmDiscard}
          onClose={() => setConfirmDiscard(false)}
          aria-labelledby="discard-assistant-title">
          <DialogTitle id="discard-assistant-title">Discard assistant setup?</DialogTitle>
          <DialogContent>
            Your unsaved job, voice and settings selections will be discarded.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDiscard(false)}>Keep editing</Button>
            <Button
              color="error"
              onClick={() => {
                onDirtyChange?.(false);
                void onClose();
              }}>
              Discard setup
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    );
  if (presentation === 'panel')
    return (
      <Stack gap={2} component="section" aria-label="Agent settings editor">
        {blockedReason && <Alert severity="warning">{blockedReason}</Alert>}
        {awaitingInitialProfile && (
          <Alert severity="warning">
            Waiting for the selected job in the current editable catalog. If it is unavailable,
            reopen settings from the current Job library.
          </Alert>
        )}
        <Box
          component="fieldset"
          disabled={
            !!blockedReason || isCreatingAgent || isWaitingForCompany || awaitingInitialProfile
          }
          sx={{
            border: 0,
            p: 0,
            m: 0,
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}>
          {[
            ['Name', nameField],
            ['Voice', voiceField],
            ['Language', languageField],
            ['Transcriber', transcriberField],
            ['Language model', modelField],
            ['Job', jobField],
          ].map(([label, field]) => (
            <Box key={String(label)} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {label}
              </Typography>
              {field}
            </Box>
          ))}
        </Box>
        {agent?.id && user?.companyId && (
          <ProfileCostEstimate
            key={`${agent.id}:${agent.updatedAt}`}
            companyId={user.companyId}
            agentId={agent.id}
          />
        )}
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            disabled={
              !!blockedReason || isCreatingAgent || isWaitingForCompany || awaitingInitialProfile
            }
            onClick={handleSave}>
            {onSaveDraft ? 'Save agent draft' : 'Save agent settings'}
          </Button>
          <Button disabled={isCreatingAgent} onClick={handleCancel}>
            Close settings
          </Button>
        </Stack>
      </Stack>
    );
  return (
    <>
      {idField}
      {startField}
      {nameField}
      {voiceField}
      {languageField}
      {transcriberField}
      {modelField}
      {jobField}
      {actionField}
    </>
  );
};
